import React, { useEffect, useState, useRef } from 'react'
import { Offcanvas, OverlayTrigger } from 'react-bootstrap'
import { formatFileSize, formatNumbers, formatSmartDecimal, getBackgroundColor, getTokenSymbol, isValidUrl } from '../../utils/helpers';
import { notifyError } from '../Toast';
import { DEFAULT_ERROR_HEADING } from '../../utils/ConstantMessages';
import { EVIDENCE_TYPE, NETWORK_FEES, VALID_EVIDENCE_TYPES, WINNER_PROPOSAL_MODE, EVIDENCE_MAX_FILE_SIZE, EVIDENCE_MAX_ACCUMULATED_SIZE } from '../../utils/constants';
import { useTheme } from '../../contexts/ThemeProvider';
import TooltipCustom from "react-bootstrap/Tooltip";
import { useSwipeClose } from '../../hooks/useSwipeClose';

const WarningIcon = () => (
    <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 003.54 21H20.46A2 2 0 0022.18 18L13.71 3.86A2 2 0 0010.29 3.86Z"
            stroke="#E8705A"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const InfoIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle cx="10" cy="10" r="9" stroke="#999" strokeWidth="1.5" />
        <path
            d="M10 9V14"
            stroke="#999"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
        <circle cx="10" cy="6.5" r="0.75" fill="#999" />
    </svg>
);

const UploadIcon = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M10 4V13M10 4L7 7M10 4L13 7"
            stroke="#999"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M3 14V15C3 16.1046 3.89543 17 5 17H15C16.1046 17 17 16.1046 17 15V14"
            stroke="#999"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const CloseIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5"
            stroke="#999"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);

const MAX_FILES = 3;

const CreateDisputeMenu = ({ showcreatedisputeOffCanvas, setShowCreateDisputeOffCanvas, handleShowCreateDispute, title, setTitle, description, setDescription, files, setFiles, isCreatorResponding, poolsdetail, mode = WINNER_PROPOSAL_MODE.DISPUTE, proposedOutcome, setProposedOutcome, handleConfirmCloseMarket, disputeOrAppealText, evidenceType, setEvidenceType, tokenSymbol, bondAmount, gasEstimateInUsdt, gasFeesSpinner, gasEstimationCloseMarket, ownermatch, gasEstimationDispute, winnerData }) => {
    const { theme } = useTheme()
    const [evidenceError, setEvidenceError] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const fileInputRef = useRef(null);

    const handleCloseCreateDispute = () => {
        setShowCreateDisputeOffCanvas(false);
        if (mode !== WINNER_PROPOSAL_MODE.RESOLUTION) {
            setTitle("");
            setDescription("");
            setFiles([]);
        }
    };

    const handleFileChange = (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const selectedFiles = Array.from(e.target.files);

        if ((files?.length ?? 0) >= MAX_FILES) {
            notifyError(DEFAULT_ERROR_HEADING, 'Maximum 3 evidence files allowed');
            e.target.value = '';
            return;
        }

        const oversized = selectedFiles.find(file => file.size > EVIDENCE_MAX_FILE_SIZE);
        if (oversized) {
            notifyError(DEFAULT_ERROR_HEADING, `"${oversized.name}" exceeds the 7 MB per-file limit`);
            e.target.value = '';
            return;
        }

        const remaining = MAX_FILES - (files?.length ?? 0);
        const filesToAdd = selectedFiles.slice(0, remaining);
        if (selectedFiles.length > remaining) {
            notifyError(DEFAULT_ERROR_HEADING, `Maximum 3 evidence files allowed. Only ${remaining} file(s) added.`);
        }

        const currentTotalSize = (files ?? []).reduce((sum, f) => sum + f.size, 0);
        const newTotalSize = currentTotalSize + filesToAdd.reduce((sum, f) => sum + f.size, 0);
        if (newTotalSize > EVIDENCE_MAX_ACCUMULATED_SIZE) {
            notifyError(DEFAULT_ERROR_HEADING, 'Total evidence size cannot exceed 25 MB');
            e.target.value = '';
            return;
        }

        setFiles([...(files ?? []), ...filesToAdd]);
        setEvidenceError(false);
        e.target.value = '';
    };

    const handleDelete = (index) => {
        setFiles((files ?? []).filter((_, i) => i !== index));
    };

    const handleSubmitDispute = () => {
        handleShowCreateDispute();
    };

    const handleBrowseClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const runGasEstimation = async () => {
        if (mode === WINNER_PROPOSAL_MODE.RESOLUTION) {
            await gasEstimationCloseMarket();
        } else if ((mode === WINNER_PROPOSAL_MODE.DISPUTE || mode === WINNER_PROPOSAL_MODE.APPEAL) && gasEstimationDispute) {
            await gasEstimationDispute();
        }
    };

    useEffect(() => {
        if (showcreatedisputeOffCanvas && mode !== WINNER_PROPOSAL_MODE.RESOLUTION && poolsdetail?.options?.length > 0 && !proposedOutcome) {
            setProposedOutcome(poolsdetail.options[0]);
        }
        showcreatedisputeOffCanvas && runGasEstimation();
    }, [showcreatedisputeOffCanvas, poolsdetail?.options]);

    const winnerDotColor = getBackgroundColor(
        poolsdetail?.options?.findIndex((opt) => opt._id === winnerData?.winnerId),
        theme,
        poolsdetail?.options?.length
    );

    const swipeRef = useSwipeClose(showcreatedisputeOffCanvas, handleCloseCreateDispute);

    return (
        <>
            <Offcanvas
                placement={window.innerWidth > 991 ? "end" : "bottom"}
                className={mode === WINNER_PROPOSAL_MODE.RESOLUTION ? "reasoningoffcanvas new-offcanvas-style-mobile closemarket-upload-newdesign" : isCreatorResponding ? "reasoningoffcanvas new-offcanvas-style-mobile" : "dispute-offcanvas reasoningoffcanvas new-offcanvas-style-mobile"}
                show={showcreatedisputeOffCanvas}
                onHide={handleCloseCreateDispute}
            >
                {
                    window.innerWidth > 991 ? (
                        <Offcanvas.Header closeButton>
                            <Offcanvas.Title>{mode === WINNER_PROPOSAL_MODE.RESOLUTION ? "Close The Market" : isCreatorResponding ? "Response to a Dispute Create" : "Dispute"}</Offcanvas.Title>
                        </Offcanvas.Header>
                    ) : (
                        <Offcanvas.Header ref={swipeRef} onClick={handleCloseCreateDispute}>
                            <span className='mobile-swap-close d-none'></span>
                        </Offcanvas.Header>
                    )
                }
                {mode === WINNER_PROPOSAL_MODE.RESOLUTION ? (
                    <>
                        <Offcanvas.Body>
                            <div className="propose-resolution-parent">
                                <div className="main-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                                        <path d="M17.025 33H9C8.20435 33 7.44129 32.6839 6.87868 32.1213C6.31607 31.5587 6 30.7957 6 30V6.00001C6 5.20436 6.31607 4.4413 6.87868 3.87869C7.44129 3.31608 8.20435 3.00001 9 3.00001H21C21.4753 2.99885 21.9462 3.09183 22.3854 3.27359C22.8246 3.45535 23.2235 3.72229 23.559 4.05901L28.941 9.44101C29.2777 9.77652 29.5447 10.1754 29.7264 10.6146C29.9082 11.0538 30.0012 11.5247 30 12V20.025" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M21 3V10.5C21 10.8978 21.158 11.2794 21.4393 11.5607C21.7206 11.842 22.1022 12 22.5 12H30" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M21 28.5H30" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M25.5 24V33" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h6>Close The Market</h6>
                                <p className="para">You need to propose an outcome for this market. The market will remain open for the duration of the dispute window.</p>
                                <h6 className="select-text">Select Outcome</h6>
                                <div className="btn-group outcome-dropdown">
                                    <button className="dropdown-toggle" type="button" id="dropdownMenuClickableInside" data-bs-toggle="dropdown" aria-expanded="false">
                                        <div className="with-circle">
                                            <span
                                                style={{
                                                    backgroundColor: getBackgroundColor(
                                                        poolsdetail?.options?.findIndex(
                                                            (opt) => opt?.optionName === proposedOutcome?.optionName
                                                        ),
                                                        theme,
                                                        poolsdetail?.options?.length
                                                    )
                                                }}
                                                className="circle-dot"
                                            ></span>
                                            {proposedOutcome ? <div className='select' style={{ maxWidth: window.innerWidth > 991 ? "450px" : "250px", overflow: "hidden", textOverflow: "ellipsis" }}>{proposedOutcome?.optionName}</div> : 'Select final outcome'}</div> <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                            <path d="M8.60172 15.5855L3.53922 10.523C3.43367 10.4174 3.37437 10.2743 3.37437 10.125C3.37437 9.97573 3.43367 9.83258 3.53922 9.72703C3.64477 9.62148 3.78792 9.56219 3.93719 9.56219C4.08645 9.56219 4.22961 9.62148 4.33516 9.72703L8.43719 13.8298L8.43719 2.8125C8.43719 2.66332 8.49645 2.52024 8.60194 2.41475C8.70743 2.30926 8.8505 2.25 8.99969 2.25C9.14887 2.25 9.29195 2.30926 9.39744 2.41475C9.50292 2.52024 9.56219 2.66332 9.56219 2.8125L9.56219 13.8298L13.6642 9.72703C13.7698 9.62148 13.9129 9.56219 14.0622 9.56219C14.2115 9.56219 14.3546 9.62148 14.4602 9.72703C14.5657 9.83258 14.625 9.97573 14.625 10.125C14.625 10.2743 14.5657 10.4174 14.4602 10.523L9.39766 15.5855C9.34542 15.6378 9.28338 15.6793 9.21509 15.7076C9.14681 15.7359 9.07361 15.7504 8.99969 15.7504C8.92577 15.7504 8.85257 15.7359 8.78428 15.7076C8.716 15.6793 8.65396 15.6378 8.60172 15.5855Z" fill="#212121" />
                                        </svg>
                                    </button>
                                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuClickableInside">
                                        {poolsdetail?.options?.map((option, index) => {
                                            const background = getBackgroundColor(
                                                index,
                                                theme,
                                                poolsdetail?.options?.length
                                            );
                                            return (
                                                <li key={index}>
                                                    <a
                                                        className={proposedOutcome?.optionName === option.optionName ? "dropdown-item selected" : "dropdown-item"}
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setProposedOutcome(option);
                                                        }}
                                                    >
                                                        <div className="left-text">
                                                            <span style={{ backgroundColor: background }} className='circle-dot'></span> {option.optionName}
                                                        </div>
                                                        {proposedOutcome?.optionName === option.optionName && (
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                                <path fillRule="evenodd" clipRule="evenodd" d="M9 18C10.1819 18 11.3522 17.7672 12.4442 17.3149C13.5361 16.8626 14.5282 16.1997 15.364 15.364C16.1997 14.5282 16.8626 13.5361 17.3149 12.4442C17.7672 11.3522 18 10.1819 18 9C18 7.8181 17.7672 6.64778 17.3149 5.55585C16.8626 4.46392 16.1997 3.47177 15.364 2.63604C14.5282 1.80031 13.5361 1.13738 12.4442 0.685084C11.3522 0.232792 10.1819 -1.76116e-08 9 0C6.61305 3.55683e-08 4.32387 0.948211 2.63604 2.63604C0.948212 4.32387 0 6.61305 0 9C0 11.3869 0.948212 13.6761 2.63604 15.364C4.32387 17.0518 6.61305 18 9 18ZM8.768 12.64L13.768 6.64L12.232 5.36L7.932 10.519L5.707 8.293L4.293 9.707L7.293 12.707L8.067 13.481L8.768 12.64Z" fill="black" />
                                                            </svg>
                                                        )}
                                                    </a>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            </div>
                            <div className="createdispute-options">
                                <div className="main-heading">
                                    <h6>Upload Evidence*<span style={{ color: "#FE5C32" }}>*</span></h6>
                                    <p className="after-upload-text">
                                        You must provide at least one file of evidence
                                    </p>
                                </div>
                                {files.map((file, index) => (
                                    <div key={index} className="afterupload">
                                        <div className="twice-text">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M8 2V10" stroke="#797979" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M11.3337 5.33333L8.00033 2L4.66699 5.33333" stroke="#797979" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="#797979" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <h6>{file?.name}</h6>
                                        </div>
                                        <a onClick={() => handleDelete(index)}><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M10.5 3.5L3.5 10.5" stroke="#797979" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M3.5 3.5L10.5 10.5" stroke="#797979" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg></a>
                                    </div>
                                ))}
                                <div className="parent-upload">
                                    <div className="upload-field">
                                        <div className="left-text">
                                            <p>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M8 2V10" stroke="#797979" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M11.3337 5.33333L8.00033 2L4.66699 5.33333" stroke="#797979" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="#797979" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                Choose a file...
                                            </p>
                                        </div>
                                        <label className='btn-upload' htmlFor="upload-resolution">Browse</label>
                                        <input
                                            type="file"
                                            className='d-none'
                                            id='upload-resolution'
                                            multiple
                                            accept={[...VALID_EVIDENCE_TYPES.photo, ...VALID_EVIDENCE_TYPES.video, ...VALID_EVIDENCE_TYPES.pdf].join(',')}
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                </div>
                                <p className="choose-file-text">Up to 3 files - PDF, images, CSV, JSON</p>
                                <div className="parent-resolution-bond">
                                    <div className="resolution-bond">
                                        <div className="upper-text" style={ownermatch ? { borderBottom: "1px solid transparent" } : {}}>
                                            <h6>Resolution Bond <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <TooltipCustom className="liquidity-provided-tooltip" id="tooltip-1">
                                                        <p>The resolution bond is calculated as a 0.1% of the volume market, minimum amount of $25 and max of 500$. <br /><br />

                                                            If a dispute is raised, your proposed outcome may be reviewed or overturned.</p>
                                                    </TooltipCustom>
                                                }
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                    <g clipPath="url(#clip0_616_29715)">
                                                        <path d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M9 12V9" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M9 6H9.0075" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </g>
                                                    <defs>
                                                        <clipPath id="clip0_616_29715">
                                                            <rect width="18" height="18" fill="white" />
                                                        </clipPath>
                                                    </defs>
                                                </svg>
                                            </OverlayTrigger></h6>
                                            <h5>
                                                {ownermatch ? (
                                                    <>
                                                        <span>0</span>
                                                        <span style={{ textDecoration: "line-through" }}>
                                                            {getTokenSymbol(tokenSymbol)}{formatNumbers(+bondAmount) ?? '...'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span>
                                                        {getTokenSymbol(tokenSymbol)}{formatNumbers(+bondAmount) ?? '...'}
                                                    </span>
                                                )}
                                            </h5>
                                        </div>
                                        {ownermatch && <div className="bottom-text">Creator Discount Applied</div>}
                                    </div>
                                    <div className="resolution-bond">
                                        <div className="upper-text">
                                            <h6>Estimated {NETWORK_FEES}</h6>
                                            <h5>
                                                {gasFeesSpinner ? (
                                                    <span className="spinner" />
                                                ) : (
                                                    `$${formatSmartDecimal(gasEstimateInUsdt)}`
                                                )}
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="twice-btns" style={{ gridTemplateColumns: "1fr" }}>
                                <button
                                    className='btn-proposal'
                                    disabled={
                                        gasFeesSpinner ||
                                        !proposedOutcome ||
                                        files?.length === 0 ||
                                        +gasEstimateInUsdt === 0
                                    }
                                    onClick={handleConfirmCloseMarket}
                                >
                                    {gasFeesSpinner ? 'Loading...' : 'Submit Proposal'}
                                </button>
                            </div>
                        </Offcanvas.Body>
                    </>
                ) : isCreatorResponding ? (
                    <>
                        <Offcanvas.Body>
                            <div className="createdispute-options">
                                <div className="option-field">
                                    <label>Title</label>
                                    <input value={title} onChange={(e) => setTitle(e.target.value)} type="text" placeholder='Enter a title for this dispute' />
                                </div>
                                <div className="option-field">
                                    <label>Description</label>
                                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder='Enter a detailed description for this dispute' />
                                </div>
                                <div className={`parent-upload${evidenceError ? ' error' : ''}`}>
                                    <div className="upload-field">
                                        <div className="left-text">
                                            <h6>Upload Up To 3 Evidence Files</h6>
                                            <p>Files Supported: JPEG, PNG, GIF, WebP, MP4, WebM, OGG, PDF, TXT, CSV, JSON. Max 7 MB per file · 25 MB total.</p>
                                        </div>
                                        <label className="btn-upload" htmlFor="upload-dispute">
                                            Select Files
                                        </label>
                                        <input
                                            type="file"
                                            className="d-none"
                                            id="upload-dispute"
                                            multiple
                                            accept={[...VALID_EVIDENCE_TYPES.photo, ...VALID_EVIDENCE_TYPES.video, ...VALID_EVIDENCE_TYPES.pdf].join(',')}
                                            onChange={handleFileChange}
                                        />
                                    </div>
                                    {files.map((file, index) => (
                                        <div key={index} className="afterupload">
                                            <div className="twice-text">
                                                <h6>{file?.name}</h6>
                                                <p>
                                                    {file.type.split("/")[1]?.toUpperCase() || "FILE"} |{" "}
                                                    {formatFileSize(file.size)}
                                                </p>
                                            </div>
                                            <a onClick={() => handleDelete(index)} style={{ cursor: "pointer" }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <path d="M7.616 20C7.168 20 6.78667 19.8426 6.472 19.528C6.15733 19.2133 6 18.8323 6 18.385V5.99998H5V4.99998H9V4.22998H15V4.99998H19V5.99998H18V18.385C18 18.845 17.846 19.2293 17.538 19.538C17.23 19.8466 16.8453 20.0006 16.384 20H7.616ZM17 5.99998H7V18.385C7 18.5643 7.05767 18.7116 7.173 18.827C7.28833 18.9423 7.436 19 7.616 19H16.385C16.5383 19 16.6793 18.936 16.808 18.808C16.9367 18.68 17.0007 18.5386 17 18.384V5.99998ZM9.808 17H10.808V7.99998H9.808V17ZM13.192 17H14.192V7.99998H13.192V17Z" fill="#C8102E" />
                                                </svg>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="bottom-btn bottom-btncreate">
                                <button
                                    disabled={!title || !description || (evidenceType === EVIDENCE_TYPE.YOUTUBE)}
                                    onClick={handleSubmitDispute}
                                    className='btn-createdispute'
                                >
                                    Response to dispute
                                </button>
                            </div>
                        </Offcanvas.Body>
                    </>
                ) : (
                    <>
                        <Offcanvas.Body className="dispute-offcanvas__body offcanvas__body__desktop">
                            <div className="dispute-offcanvas__icon-wrapper">
                                <div className="dispute-offcanvas__icon">
                                    <WarningIcon />
                                </div>
                            </div>

                            <h2 className="dispute-offcanvas__title">
                                You are about to create a {disputeOrAppealText?.toLowerCase() || 'dispute'}
                            </h2>
                            <p className="dispute-offcanvas__subtitle">
                                Select the correct outcome or flag for early closure.
                            </p>

                            {winnerData && (
                                <div className="dispute-offcanvas__proposed">
                                    <div className="dispute-offcanvas__proposed-left">
                                        <span className="dispute-offcanvas__proposed-label">
                                            PROPOSED OUTCOME
                                        </span>
                                        <div className="dispute-offcanvas__proposed-value">
                                            <span
                                                className="dispute-offcanvas__dot"
                                                style={{ backgroundColor: winnerDotColor }}
                                            />
                                            {winnerData?.winnerOption || "—"}
                                        </div>
                                    </div>
                                    <span className="dispute-offcanvas__disputing-badge">DISPUTING</span>
                                </div>
                            )}

                            <div className="option-field">
                                <label>Dispute Title</label>
                                <input type="text" placeholder='Dispute Title' value={title} onChange={(e) => setTitle(e.target.value)} />
                            </div>
                            <div className="option-field">
                                <label>Dispute Description</label>
                                <textarea placeholder='Dispute Description' value={description} onChange={(e) => setDescription(e.target.value)} />
                            </div>

                            <div className="dispute-offcanvas__evidence-label">
                                <span>Upload Evidence</span>
                                <span className="dispute-offcanvas__required">*</span>
                            </div>
                            <p className="after-upload-text">
                               You must provide at least one file of evidence
                            </p>

                            {(files?.length ?? 0) > 0 && (
                                <div className="dispute-offcanvas__file-list">
                                    {files.map((file, index) => (
                                        <div key={index} className="dispute-offcanvas__file-item">
                                            <div className="dispute-offcanvas__file-item-left">
                                                <UploadIcon />
                                                <span className="dispute-offcanvas__file-name">
                                                    {file.name}
                                                </span>
                                            </div>
                                            <button
                                                className="dispute-offcanvas__file-remove"
                                                onClick={() => handleDelete(index)}
                                            >
                                                <CloseIcon />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {(files?.length ?? 0) < MAX_FILES && (
                                <div
                                    className="dispute-offcanvas__file-upload"
                                    onClick={handleBrowseClick}
                                >
                                    <div className="dispute-offcanvas__file-left">
                                        <UploadIcon />
                                        <span className="dispute-offcanvas__file-placeholder">
                                            Choose a file...
                                        </span>
                                    </div>
                                    <button
                                        className="dispute-offcanvas__browse-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBrowseClick();
                                        }}
                                    >
                                        Browse
                                    </button>
                                </div>
                            )}

                            <input
                                type="file"
                                ref={fileInputRef}
                                accept=".pdf,.png,.jpg,.jpeg,.csv,.json"
                                onChange={handleFileChange}
                                hidden
                            />

                            {
                                (files?.length ?? 0) < MAX_FILES && (
                                    <p className="dispute-offcanvas__file-hint">
                                        Up to 3 files - PDF, images, CSV, JSON
                                    </p>
                                )
                            }



                            <div className="dispute-offcanvas__fees">
                                <div className="dispute-offcanvas__fee-row dispute-offcanvas__fee-row--bond">
                                    <div className="dispute-offcanvas__fee-label">
                                        Dispute Bond
                                        <span
                                            className="dispute-offcanvas__info-icon"
                                            onClick={() => setShowTooltip(!showTooltip)}
                                            onMouseEnter={() => setShowTooltip(true)}
                                            onMouseLeave={() => setShowTooltip(false)}
                                        >
                                            <InfoIcon />
                                        </span>
                                        {showTooltip && (
                                            <div className="dispute-offcanvas__tooltip">
                                                You will be charged with 0.1% bond fee of the market's volume
                                                - the minimum amount is 25$ and the maximum is 500$
                                            </div>
                                        )}
                                    </div>
                                    <span className="dispute-offcanvas__fee-value">
                                        {getTokenSymbol(tokenSymbol)}{formatNumbers(+bondAmount) || "—"}
                                    </span>
                                </div>
                                <div className="dispute-offcanvas__fee-row dispute-offcanvas__fee-row--network">
                                    <span className="dispute-offcanvas__fee-label" style={{ color: "var(--para)" }}>Network Fee</span>
                                    <span className="dispute-offcanvas__fee-value" style={{ color: "var(--para)" }}>
                                        {gasFeesSpinner
                                            ? <span className="spinner" />
                                            : `$${formatSmartDecimal(gasEstimateInUsdt) || "—"}`}
                                    </span>
                                </div>
                            </div>
                            <p className="dispute-offcanvas__refund-note">
                                Bond refunded if dispute is successful
                            </p>

                            <button
                                className="dispute-offcanvas__submit-btn"
                                onClick={handleSubmitDispute}
                                disabled={gasFeesSpinner || !title || !description || (files?.length ?? 0) === 0}
                            >
                                {gasFeesSpinner ? 'Loading...' : 'SUBMIT PROPOSAL'}
                            </button>
                        </Offcanvas.Body>
                    </>
                )}
            </Offcanvas>
        </>
    )
}

export default CreateDisputeMenu
